import {Link} from "react-router-dom";
import {Tag, Tooltip, Space, Typography} from 'antd';
import {Copy, CirclePlay, CirclePause, CircleSlash2, ThermometerSun} from 'lucide-react';
import {copyToClipboardUtils, deviceOSArchitectureIcon, deviceStatusIcon, deviceStatusInactiveText} from '../../utils';
import {deviceStatusInactiveIcons} from "../../utils/deviceUtils";
import {images} from "../../constants";
import {AiFillPushpin} from "react-icons/ai";
import styles from "../Companies/CompaniesColumns.module.scss";
import {RiPushpin2Fill} from "react-icons/ri";
import {deviceTempColor, formatTerminalOrder} from "../../utils/device/deviceUtils";
import DeviceOperations from "./DeviceDetail/DeviceOperations";

const DevicesColumns = (t, isUserStaff, handleSaveMarket, handleDeleteSaveMarket, totalDevices, totalMarkets, companyInn, token, isMobile = false) => {
    const {Text} = Typography;

    return [
        {
            title: (
                <span>
                    {t('pages.devices.listColumns.column14')}
                    <span style={{fontWeight: 'normal', color: '#999'}}>
                        {' '}({totalMarkets})
                    </span>
                </span>
            ),
            dataIndex: 'market_name',
            fixed: isMobile ? undefined : 'left',
            render: (text, record) => {
                return {
                    children: (
                        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                            {record.showGroupIcon && (
                                <Tooltip title="Add to Saved Markets">
                                    {record.is_saved ? (
                                        <RiPushpin2Fill
                                            size={18}
                                            className={styles.heartIconSaved}
                                            onClick={() => handleDeleteSaveMarket(record)}
                                        />
                                    ) : (
                                        <AiFillPushpin
                                            size={18}
                                            className={styles.heartIcon}
                                            onClick={() => handleSaveMarket(record)}
                                        />
                                    )}
                                </Tooltip>
                            )}
                            <span>{text}</span>
                        </div>
                    ),
                    props: {
                        rowSpan: record.rowSpan,
                    },
                };
            },
        },
        {
            title: "",
            key: "copy",
            render: (_, record) => (
                <Tooltip key={record.id} title={t("common.copyToClipboardText")}>
                    <Copy
                        size={14}
                        style={{cursor: "pointer", color: "gray"}}
                        onClick={() => copyToClipboardUtils("Device", record, ["teamviewer"])}
                    />
                </Tooltip>
            ),
        },
        ...(isUserStaff
            ? [
                {
                    title: "",
                    key: "operations",
                    render: (_, record) => (
                        <DeviceOperations
                            serialNumber={record.device_serial}
                            token={token}
                            iconOnly
                            buttonClassName="device-list__operation-button"
                            onTriggerClick={(event) => event.stopPropagation()}
                        />
                    ),
                }
            ]
            : []),
        {
            title: t('pages.devices.listColumns.column1'),
            dataIndex: 'is_active_period',
            filters: [
                {text: 'Active', value: 'active'},
                {text: 'Minute', value: 'minute'},
                {text: 'Hour', value: 'hour'},
                {text: 'Day', value: 'day'},
            ],
            filterMultiple: false,
            render: (text, record) => (
                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    {record.is_active ? (
                        <Tooltip key="active" title="Device is Active">
                            <img
                                src={images.deviceActive}
                                alt="Active"
                                style={{width: 18, height: 18}}
                            />
                        </Tooltip>
                    ) : (
                        <Tooltip
                            key="inactive"
                            title={`Inactive ${deviceStatusInactiveText[record.is_active_time] || ''}`}
                        >
                            <img
                                src={deviceStatusInactiveIcons[record.is_active_time] || deviceStatusInactiveIcons.default}
                                alt={`Inactive ${record.is_active_time}`}
                                style={{width: 18, height: 18}}
                            />
                        </Tooltip>
                    )}
                    <Tooltip
                        key="shift"
                        title={record.is_shift_open
                            ? t('pages.devices.listColumns.shiftOpen')
                            : t('pages.devices.listColumns.shiftClosed')}
                    >
                        {
                            record.is_shift_open ? (
                                <CirclePlay
                                    color={'green'}
                                    size={18}
                                    style={{marginLeft: 4, verticalAlign: 'middle'}}
                                />
                            ) : (
                                <CirclePause
                                    color={'red'}
                                    size={18}
                                    style={{marginLeft: 4, verticalAlign: 'middle'}}
                                />
                            )
                        }
                    </Tooltip>
                </div>
            ),
        },
        {
            title: 'Temp',
            dataIndex: 'temp',
            orderIndex: 'device_status__temperature',
            sorter: true,
            render: (_, {temp}) => (
                <Tag color={deviceTempColor(temp)}>
                    <ThermometerSun size={12}/> {temp ?? 'N/A'}°C
                </Tag>
            ),
        },
        {
            title: `${t('pages.devices.listColumns.column8')}`,
            dataIndex: 'device_serial__os_architecture_bit',
            // sorter: true,
            orderIndex: 'runtime__version_number',
            filters: [
                {text: 'x32-bit', value: 32},
                {text: 'x64-bit', value: 64},
            ],
            filterMultiple: false,
            onFilter: (value, record) =>
                record.device_serial__os_architecture_bit === value,
            render: (_, record) => {
                const version = record.version_number;
                const arch = record.device_serial__os_architecture_bit;
                const applet = record.applet_version;
                const tags = (
                    <>
                        {arch && (
                            <Tag color={deviceOSArchitectureIcon(arch)}>
                                x{arch}
                            </Tag>
                        )}
                        {applet && (
                            <Tag color='green'>
                                {applet}
                            </Tag>
                        )}
                    </>
                );

                if (isMobile) {
                    return (
                        <div className="device-list__version-cell">
                            <Text strong className="device-list__version-text">
                                {version}
                            </Text>
                            <div className="device-list__version-tags">
                                {tags}
                            </div>
                        </div>
                    );
                }

                return (
                    <Space size="small">
                        <Text strong>{version}</Text>
                        {tags}
                    </Space>
                );
            },
        },
        {
            title: t('pages.devices.listColumns.column11'),
            dataIndex: 'device_serial__status',
            filters: [
                {text: t('common.active'), value: 'ACTIVE'},
                {text: t('common.inactive'), value: 'INACTIVE'},
                {text: t('common.disconnected'), value: 'DISCONNECTED'},
            ],
            filterMultiple: false,
            onFilter: (value, record) => record.device_serial__status === value,
            render: (_, {device_serial__status}) => (
                <>
                    {[device_serial__status].map(tag => (
                        <Tag color={deviceStatusIcon(tag)} key={tag}>
                            {`${String(tag)}`.toUpperCase()}
                        </Tag>
                    ))}
                </>
            ),
        },
        {
            title: t('pages.devices.listColumns.column17'),
            dataIndex: 'pos_terminal.type',
            filterMultiple: false,
            render: (_, record) => {
                const terminalData = record.pos_terminal;
                const type = record.pos_terminal?.type;
                const toolTipContent = (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                        <div><strong>Type:</strong> {terminalData?.type?.toString().toUpperCase()}</div>
                        <div><strong>Model:</strong> {terminalData?.terminal_model}</div>
                        <div><strong>Serial Number:</strong> {terminalData?.terminal_serial_number}</div>
                        <div><strong>TMS ID:</strong> {terminalData?.tms_id}</div>
                        <div><strong>IP:</strong> {terminalData?.ip_address}</div>
                        <div><strong>Mask:</strong> {terminalData?.ip_mask}</div>
                        <div><strong>Gateway:</strong> {terminalData?.gateway}</div>
                    </div>
                );
                const icons = {
                    pax: {src: images.paxTerminalIcon, title: toolTipContent},
                    ingenico: {src: images.ingenicoTerminalIcon, title: toolTipContent},
                };
                const icon = icons[type];

                return (
                    <Tooltip title={icon?.title ?? 'Unknown Terminal'}>
                        {icon ? (
                            <img src={icon.src} alt={icon.title} style={{width: 20, height: 38}}/>
                        ) : (
                            <CircleSlash2 size={18} strokeWidth={1.8} style={{color: 'red', verticalAlign: 'middle'}}/>
                        )}
                    </Tooltip>
                );
            },
        },
        {
            title: (
                <span style={{textDecoration: 'underline'}}>
                    {t('pages.devices.listColumns.column2')}
                    <span style={{fontWeight: 'normal', color: '#999'}}>
                        {' '}({totalDevices})
                    </span>
                </span>
            ),
            dataIndex: 'device_serial',
            sorter: true,
            orderIndex: 'device_serial_number',
            render: (text, record) => (
                <Link to={`/device/detail/${record.device_serial}`}>{text}</Link>
            ),
        },
        ...(isUserStaff && !companyInn
            ? [
                {
                    title: t('pages.devices.listColumns.column10'),
                    dataIndex: 'company_name',
                    sorter: false,
                }
            ]
            : []),
        {
            title: t('pages.devices.listColumns.column15'),
            dataIndex: 'device_serial__fbox_version',
            render: (text, record) => (
                record.device_serial__fbox_version === 'v1' ? (
                    <Tag color='#108ee9'
                         className="version-tag">
                        POS
                    </Tag>
                ) : (
                    <Tag color='#ff5501'
                         className="version-tag">
                        SCO
                    </Tag>
                )
            )
        },
        {
            title: t('pages.devices.listColumns.column3'),
            dataIndex: 'device_ip_addr',
            sorter: false,
        },
        {
            title: t('pages.devices.listColumns.column4'),
            dataIndex: 'terminal_id',
            sorter: false,
            render: (_, record) => {
                const terminalId = record.terminal_id;
                const orderNumber = record.last_order_number;

                return (
                    <Space size="small">
                        <Text>{formatTerminalOrder(terminalId, orderNumber)}</Text>
                    </Space>
                );
            },
        },
        {
            title: t('pages.devices.listColumns.column5'),
            dataIndex: 'orders_not_sent_count',
            sorter: true,
            orderIndex: 'device_status__orders_not_sent_count',
            render: (_, {orders_not_sent_count}) => (
                <>
                    {[orders_not_sent_count].map(tag => (
                        <Tag color={tag <= 10 ? 'green' : 'red'} key={tag}>
                            {`${String(tag)}`.toUpperCase()}
                        </Tag>
                    ))}
                </>
            ),
        },
        {
            title: t('pages.devices.listColumns.column6'),
            dataIndex: 'z_report_left_count',
            sorter: true,
            orderIndex: 'device_status__z_report_left_count',
            render: (_, {z_report_left_count}) => (
                <>
                    {[z_report_left_count].map(tag => (
                        <Tag color={tag <= 50 ? 'red' : 'green'} key={tag}>
                            {`${String(tag)}`.toUpperCase()}
                        </Tag>
                    ))}
                </>
            ),
        },
        {
            title: t('pages.devices.listColumns.column7'),
            dataIndex: 'updated_date',
            orderIndex: 'device_status__updated_date',
            sorter: (a, b) => new Date(a.updated_date) - new Date(b.updated_date),
        },
        {
            title: t('pages.devices.listColumns.column16'),
            dataIndex: 'device_serial__last_order_time',
            // sorter: (a, b) => new Date(a.device_serial__last_order_time) - new Date(b.device_serial__last_order_time),
        },
        // ...(isUserStaff
        //         ? [
        //             {
        //                 title: t('pages.devices.listColumns.column9'),
        //                 dataIndex: 'end_date',
        //                 orderIndex: 'device_serial__end_date',
        //                 sorter: (a, b) => new Date(a.end_date) - new Date(b.end_date),
        //             }
        //         ]
        //         : []
        // ),
        ...(isUserStaff
                ? [
                    {
                        title: t('pages.devices.listColumns.column12'),
                        dataIndex: 'teamviewer',
                        render: (text, record) => {
                            return (
                                <a
                                    href={`https://start.teamviewer.com/${record.teamviewer}`}
                                    onClick={(e) => e.stopPropagation()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="version-number-link"
                                >
                                    {text}
                                </a>
                            );
                        },
                    }
                ]
                : []
        ),
    ]
};

export default DevicesColumns;
